import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  type Cell,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Header,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowUp, ArrowUpDown, GripHorizontal } from 'lucide-react'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Button } from '@/shared/components/ui/button'

interface VirtualizedTableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData>[]
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  fetchNextPage?: () => void
  enableSorting?: boolean
  enableColumnResizing?: boolean
  enableColumnReordering?: boolean
  enableRowSelection?: boolean
  enableColumnPinning?: boolean
  initialColumnPinning?: ColumnPinningState
  rowHeight?: number
  headerHeight?: number
  containerHeight?: string
  emptyMessage?: string
  loadingMessage?: string
  className?: string
  headerClassName?: string
  rowClassName?: string
  cellClassName?: string
  onRowClick?: (row: TData) => void
  skeletonRows?: number
}

// Draggable Cell Component
// biome-ignore lint/suspicious/noExplicitAny: required for generic table component
interface DragAlongCellProps<TData = any> {
  cell: Cell<TData, unknown>
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

// biome-ignore lint/suspicious/noExplicitAny: required for generic table component
function DragAlongCell<TData = any>({
  cell,
  children,
  className,
  style,
}: DragAlongCellProps<TData>) {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  })

  return (
    <td
      ref={setNodeRef}
      className={className}
      style={{
        ...style,
        opacity: isDragging ? 0.8 : 1,
        position: cell.column.getIsPinned() ? 'sticky' : 'relative',
        transform: CSS.Translate.toString(transform),
        transition: 'width transform 0.2s ease-in-out',
        width: cell.column.getSize(),
        zIndex: isDragging ? 100 : cell.column.getIsPinned() ? 12 : 1,
      }}
    >
      {children}
    </td>
  )
}

// Draggable Header Component
// biome-ignore lint/suspicious/noExplicitAny: required for generic table component
interface DraggableTableHeaderProps<TData = any> {
  header: Header<TData, unknown>
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  enableReordering?: boolean
}

// biome-ignore lint/suspicious/noExplicitAny: required for generic table component
function DraggableTableHeader<TData = any>({
  header,
  children,
  className,
  style,
  enableReordering,
}: DraggableTableHeaderProps<TData>) {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    })

  return (
    <th
      ref={setNodeRef}
      colSpan={header.colSpan}
      className={`${className} group`}
      style={{
        ...style,
        opacity: isDragging ? 0.8 : 1,
        position: 'sticky',
        transform: CSS.Translate.toString(transform),
        transition: 'width transform 0.2s ease-in-out',
        width: header.column.getSize(),
        zIndex: isDragging ? 100 : header.column.getIsPinned() ? 15 : 14,
      }}
    >
      <div className='flex items-center gap-2'>
        {children}
        {enableReordering && !header.column.getIsPinned() && (
          <GripHorizontal
            className='w-4 h-4 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity text-slate-400'
            {...attributes}
            {...listeners}
          />
        )}
      </div>
    </th>
  )
}

// Sorting icon helper
function getSortingIcon(sorted: false | 'asc' | 'desc') {
  if (sorted === 'asc') return <ArrowUp className='w-4 h-4' />
  if (sorted === 'desc') return <ArrowDown className='w-4 h-4' />
  return <ArrowUpDown className='w-4 h-4' />
}

// Skeleton row component for loading states
function SkeletonTableRow({
  columns,
  rowHeight,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: required for generic table component
  columns: ColumnDef<any>[]
  rowHeight: number
}) {
  return (
    <tr className='animate-pulse' style={{ height: rowHeight }}>
      {columns.map((_, index) => (
        <td
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          key={index}
          className='p-3 text-sm border-b border-slate-200 dark:border-slate-700'
          style={{
            width: `${100 / columns.length}%`,
          }}
        >
          <div className='flex items-center space-x-2'>
            <div
              className={`h-4 bg-slate-300 dark:bg-slate-600 rounded ${
                index === 0
                  ? 'w-3/4'
                  : index === columns.length - 1
                    ? 'w-8'
                    : 'w-1/2'
              }`}
            />
          </div>
        </td>
      ))}
    </tr>
  )
}

export const VirtualizedTable = forwardRef<
  HTMLDivElement | null,
  // biome-ignore lint/suspicious/noExplicitAny: required for generic table component
  VirtualizedTableProps<any>
>(
  (
    {
      data,
      columns,
      isLoading = false,
      isFetchingNextPage = false,
      hasNextPage = false,
      fetchNextPage,
      enableSorting = true,
      enableColumnResizing = true,
      enableColumnReordering = true,
      enableColumnPinning = true,
      initialColumnPinning = {},
      rowHeight = 60,
      headerHeight = 48,
      containerHeight = 'calc(100vh - 200px)',
      emptyMessage = 'No records to show',
      loadingMessage = 'Loading more...',
      className = '',
      headerClassName = 'bg-slate-100 dark:bg-slate-800 p-3 text-left font-normal text-slate-900 dark:text-slate-100 sticky top-0 z-10',
      rowClassName = '',
      cellClassName = 'p-3 text-sm border-b border-slate-200 dark:border-slate-700',
      onRowClick,
      skeletonRows = 4,
    },
    ref
  ) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnOrder, setColumnOrder] = useState<string[]>([])
    const [columnPinning, setColumnPinning] =
      useState<ColumnPinningState>(initialColumnPinning)
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const tableContainerRef = useRef<HTMLDivElement>(null)

    const table = useReactTable({
      data,
      columns,
      state: {
        sorting,
        columnOrder,
        columnPinning,
        columnFilters,
      },
      onSortingChange: enableSorting ? setSorting : undefined,
      onColumnOrderChange: enableColumnReordering ? setColumnOrder : undefined,
      onColumnPinningChange: enableColumnPinning ? setColumnPinning : undefined,
      onColumnFiltersChange: setColumnFilters,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
      columnResizeMode: enableColumnResizing ? 'onChange' : undefined,
      enableColumnResizing,
      enableSorting,
      enableColumnPinning,
    })

    // Initialize column order
    useEffect(() => {
      if (columnOrder.length === 0) {
        setColumnOrder(table.getAllColumns().map(col => col.id))
      }
    }, [table, columnOrder.length])

    // Expose table container to parent
    useImperativeHandle(
      ref,
      () => tableContainerRef.current as HTMLDivElement,
      []
    )

    // Handle drag end
    function handleDragEnd(event: DragEndEvent) {
      const { active, over } = event
      if (active && over && active.id !== over.id) {
        setColumnOrder(columnOrder => {
          const oldIndex = columnOrder.indexOf(active.id as string)
          const newIndex = columnOrder.indexOf(over.id as string)
          return arrayMove(columnOrder, oldIndex, newIndex)
        })
      }
    }

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    )

    // Set up virtualization
    const rowVirtualizer = useVirtualizer({
      count: table.getRowModel().rows.length,
      getScrollElement: () => tableContainerRef.current,
      estimateSize: () => rowHeight,
      overscan: 10,
    })

    // Handle infinite scroll
    useEffect(() => {
      const container = tableContainerRef.current
      if (!container) return

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = container
        if (
          scrollHeight - scrollTop - clientHeight < 100 &&
          hasNextPage &&
          !isFetchingNextPage
        ) {
          fetchNextPage?.()
        }
      }

      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }, [hasNextPage, isFetchingNextPage, fetchNextPage])

    return (
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <div className={`w-full ${className}`}>
          <div
            ref={tableContainerRef}
            className='overflow-auto relative border rounded-lg'
            style={{ height: containerHeight }}
          >
            <table
              className='w-full text-sm'
              style={{ borderCollapse: 'separate', borderSpacing: 0 }}
            >
              {/* Table Header */}
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    <SortableContext
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map(header => (
                        <DraggableTableHeader
                          key={header.id}
                          header={header}
                          className={headerClassName}
                          enableReordering={enableColumnReordering}
                          style={{
                            width: header.getSize(),
                            minWidth: header.getSize(),
                            maxWidth: header.getSize(),
                            left:
                              header.column.getIsPinned() === 'left'
                                ? header.column.getStart('left')
                                : undefined,
                          }}
                        >
                          {header.isPlaceholder ? null : (
                            <div className='flex items-center gap-2'>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {header.column.getCanSort() && enableSorting && (
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  className='h-6 w-6 p-0'
                                  onClick={header.column.getToggleSortingHandler()}
                                >
                                  {getSortingIcon(header.column.getIsSorted())}
                                </Button>
                              )}
                            </div>
                          )}
                          {/* Resize handle */}
                          {header.column.getCanResize() &&
                            enableColumnResizing && (
                              // biome-ignore lint/a11y/noStaticElementInteractions: mouse-only resize handle
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className='absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity'
                                style={{
                                  userSelect: 'none',
                                  touchAction: 'none',
                                }}
                              />
                            )}
                        </DraggableTableHeader>
                      ))}
                    </SortableContext>
                  </tr>
                ))}
              </thead>

              {/* Table Body */}
              <tbody>
                {isLoading && !table.getRowModel().rows.length ? (
                  Array.from({ length: skeletonRows }).map((_, index) => (
                    <SkeletonTableRow
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={`skeleton-${index}`}
                      columns={columns}
                      rowHeight={rowHeight}
                    />
                  ))
                ) : table.getRowModel().rows.length ? null : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className='text-center p-8 text-slate-500 italic'
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const row = table.getRowModel().rows[virtualRow.index]
                    if (!row) return null

                    return (
                      <tr
                        key={row.id}
                        className={`${rowClassName} ${onRowClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50' : ''}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: `${table.getCenterTotalSize() + (table.getLeftTotalSize() || 0) + (table.getRightTotalSize() || 0)}px`,
                          transform: `translateY(${virtualRow.start + headerHeight}px)`,
                          height: `${rowHeight}px`,
                        }}
                        onClick={() => onRowClick?.(row.original)}
                      >
                        <SortableContext
                          items={columnOrder}
                          strategy={horizontalListSortingStrategy}
                        >
                          {row.getVisibleCells().map(cell => (
                            <DragAlongCell
                              key={cell.id}
                              cell={cell}
                              className={`${cellClassName} ${cell.column.getIsPinned() ? 'bg-white dark:bg-slate-950' : 'bg-transparent'}`}
                              style={{
                                width: cell.column.getSize(),
                                minWidth: cell.column.getSize(),
                                maxWidth: cell.column.getSize(),
                                left:
                                  cell.column.getIsPinned() === 'left'
                                    ? cell.column.getStart('left')
                                    : undefined,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </DragAlongCell>
                          ))}
                        </SortableContext>
                      </tr>
                    )
                  })}
                {/* Loading indicator for infinite scroll */}
                {isFetchingNextPage && (
                  <tr
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: `translate(-50%, ${rowVirtualizer.getTotalSize() + headerHeight}px)`,
                      width: 'auto',
                      zIndex: 20,
                    }}
                  >
                    <td className='p-4 text-slate-500'>
                      <div className='flex items-center justify-center space-x-2 whitespace-nowrap'>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600'></div>
                        <span className='text-sm'>{loadingMessage}</span>
                      </div>
                    </td>
                  </tr>
                )}
                {/* Virtual spacer */}
                <tr
                  style={{
                    height: `${rowVirtualizer.getTotalSize() + headerHeight}px`,
                  }}
                >
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </DndContext>
    )
  }
)

VirtualizedTable.displayName = 'VirtualizedTable'
